/* eslint-disable eslint-comments/disable-enable-pair */
/* eslint-disable @typescript-eslint/naming-convention */
import {TAX_SETTINGS} from '@advisor/util/constants';

export default class Position {
  // eslint-disable-next-line max-statements
  constructor() {
    this.assetClass = null;
    this.compositeId = 0;
    this.compositeName = null;
    this.composites = [];
    this.isCompositionWeightCalculated = false;
    this.cusip = null;
    this.expense = 0;
    this.lastUpdate = null;
    this.lastUpdateUser = null;
    this.lineExpense = 0;
    this.lineId = 0;
    this.lineOrder = 0;
    this.modelId = 0;
    this.sedol = null;
    this.tacId = 0;
    this.taxTitle = null;
    this.ticker = null;
    this.tickerId = -1;
    this.tickerName = null;
    this.tickerTax = 1;
    this.tickerType = 'Mixed';
    this.tickerTypeId = 0;
    this.tickerWeight = 0.0025;
    this.tempWeight = this.tickerWeight * 100;
    this.isNew = true;
    this.uniqueKey = null;
    this.baseCompositeExpense = 0;
    this.isEdit = false;
    this.isTaxEdit = false;
  }

  set weight(val) {
    if (!Number.isNaN(val) && val > 0) {
      this.tickerWeight = (val / 100).toFixed(4);
      if (this.isComposite()) {
        this.updateCompositesWeight();
      }
    }
  }

  get weight() {
    let actualWeight = this.tickerWeight * 100;
    const strTickerWeight = actualWeight.toString();
    const arrTickerWeight = strTickerWeight.split('.');

    if (arrTickerWeight[1] && arrTickerWeight[1].length > 2) {
      actualWeight = actualWeight.toFixed(2);
    }

    return actualWeight;
  }

  set _tickerName(strName) {
    if (this.isComposite()) {
      this.compositeName = strName;
    } else {
      this.ticker = strName;
    }
  }

  get _tickerName() {
    return this.isComposite() ? this.compositeName : this.ticker;
  }

  get _assetClassName() {
    return this.assetClass ? this.assetClass : 'NA: Asset Class';
  }

  set _assetClassName(strAssetClass) {
    this.assetClass = strAssetClass;
  }

  get _securityType() {
    if (this.isComposite()) {
      return 'Sleeve';
    }

    return this.tickerType ? this.tickerType : '';
  }

  set _securityType(strTickerType) {
    this.tickerType = strTickerType;
  }

  get _name() {
    return this.isComposite() ? this.compositeName : this.tickerName;
  }

  set _name(strName) {
    if (this.isComposite()) {
      this.compositeName = strName;
    } else {
      this.tickerName = strName;
    }
  }

  get _expense() {
    return this.isComposite() ? this.expense : this.expense * 100;
  }

  set _expense(numExpense) {
    if (this.isComposite()) {
      this.expense = numExpense;
    } else {
      this.tickerName = numExpense / 100;
    }
  }

  /**
   *
   * @param {Object} objPosition
   * @param {Boolean} isNew
   * @returns {Position}
   */
  static make(objPosition, isNew = false) {
    const newPosition = new Position();
    /**
     * @type {Position}
     */
    let mixPosition = Object.assign(newPosition, objPosition);

    mixPosition.tempWeight = Number(mixPosition.weight).toFixed(2);

    if (mixPosition.isCompositionWeightCalculated) {
      /**
       * We are getting calculated compositions weight so we need to find actual weight of compositions for further calculations
       */
      mixPosition = Position.updateCalculatedCompositesWeight(mixPosition);
    }

    mixPosition.isNew = isNew;

    mixPosition.tickerTax = mixPosition.tickerTax || 1;

    return mixPosition;
  }

  updateCompositesWeight() {
    return new Promise((resolve) => {
      this.composites.map((composite) => {
        const tempComposite = composite;

        tempComposite.weight = this.weight * tempComposite.tickerWeight;

        return tempComposite;
      });
      resolve();
    });
  }

  static updateCalculatedCompositesWeight(sleeve) {
    const mixPosition = sleeve;

    mixPosition.composites.map((composite) => {
      const tempComposite = composite;

      tempComposite.weight = tempComposite.tickerWeight * 100;
      tempComposite.tickerWeight = (tempComposite.tickerWeight * 100) / mixPosition.weight;

      return tempComposite;
    });

    return mixPosition;
  }

  /**
   * This calculates expense of sleeve
   */
  getCompositeExpense() {
    let childExpenseSum = 0;

    childExpenseSum = this.composites.reduce((sum, child) => {
      let tempSum = sum;

      tempSum += parseFloat(child.expense) * parseFloat(child.tickerWeight) * 100;

      return tempSum;
    }, 0);
    this.baseCompositeExpense = childExpenseSum;
    this.expense = this.weight > 0 ? childExpenseSum / (parseFloat(this.weight) * 100) : 0;
  }

  /**
   * This calculates the tax setting status of sleeve
   * The tax setting of sleeve is depend on the status of its composites.
   * This should be called only if position is sleeve
   * @type {Operation}
   *
   * @returns {void} this updates taxStatus
   */
  getCompositeTaxSetting() {
    const taxStatuses = new Set();

    this.composites.forEach((child) => {
      taxStatuses.add(child.tickerTax);
    });

    /**
     * IF not any => tax = Mixed
     */
    if (taxStatuses.size === 0) {
      this.tickerTax = TAX_SETTINGS.STATUSES.Mixed;
    }

    /**
     * IF no Both but at least one of the other 2 present => tax = Mixed
     */
    if (
      taxStatuses.has(TAX_SETTINGS.STATUSES.Both) === false &&
      (taxStatuses.has(TAX_SETTINGS.STATUSES.Taxable) === true ||
        taxStatuses.has(TAX_SETTINGS.STATUSES.TaxAdvantaged) === true)
    ) {
      this.tickerTax = TAX_SETTINGS.STATUSES.Mixed;
    }

    /**
     * IF no Both but both Taxable and Tax-Advantaged present => tax = Both
     */
    if (
      taxStatuses.has(TAX_SETTINGS.STATUSES.Both) === false &&
      taxStatuses.has(TAX_SETTINGS.STATUSES.Taxable) === true &&
      taxStatuses.has(TAX_SETTINGS.STATUSES.TaxAdvantaged) === true
    ) {
      this.tickerTax = TAX_SETTINGS.STATUSES.Both;
    }

    /**
     * IF Both is present but none of the other 2 => tax = Both
     */
    if (
      taxStatuses.has(TAX_SETTINGS.STATUSES.Both) === true &&
      taxStatuses.has(TAX_SETTINGS.STATUSES.Taxable) === false &&
      taxStatuses.has(TAX_SETTINGS.STATUSES.TaxAdvantaged) === false
    ) {
      this.tickerTax = TAX_SETTINGS.STATUSES.Both;
    }
  }

  /**
   * This will check given position is new and which has not ticker selected
   * @type {Helper}
   *
   * @returns {Boolean} is this composite or not
   */
  isNewPosition() {
    return this.compositeId === 0 && this.tickerId === -1;
  }

  /**
   * This will check given position is sleeve or ticker
   * @type {Helper}
   *
   * @returns {Boolean} is this composite or not
   */
  isComposite() {
    return this.compositeId !== 0;
  }

  /**
   * This function checks the sleeves having composites or not.
   * @type {Helper}
   * @returns {Boolean}
   */
  hasComposites() {
    return this.isComposite() && Array.isArray(this.composites) && this.composites.length > 0;
  }
}
